import React, { forwardRef } from "react";
import { FloatingPortal, FloatingFocusManager } from "@floating-ui/react";
import { useLightboxContext } from "../hooks/useLightboxContext";
import { StyledLightboxPanel, StyledLightboxPanelContent, StyledLightboxTitle } from "../styled";
import type { LightboxContentProps } from "../types";

/**
 * Cascade mode renderer.
 * Each panel is an independent floating element with its own shadow and positioning.
 * Standard floating-ui behavior — dismiss, focus management, and positioning are per-panel.
 */
export const CascadeContent = forwardRef<HTMLDivElement, LightboxContentProps>(
    function CascadeContent(
        {
            children,
            title,
            className,
            style,
            trapFocus = true,
            initialFocus = 0,
            onEscapeKeyDown,
            cascade = false,
        },
        forwardedRef
    ) {
        const {
            refs,
            floatingStyles,
            getFloatingProps,
            context,
            isMounted,
            transitionStyles,
            contentId,
        } = useLightboxContext();

        if (!isMounted) return null;

        const setRef = (node: HTMLDivElement | null) => {
            refs.setFloating(node);
            if (typeof forwardedRef === "function") {
                forwardedRef(node);
            } else if (forwardedRef) {
                forwardedRef.current = node;
            }
        };

        // zIndex belongs on the outer shell (for portal stacking).
        // width, overflow, etc. belong on the inner content panel.
        const { zIndex, overflow, ...restContentStyle } = style || {} as any;
        const contentStyle = overflow ? { overflow, ...restContentStyle } : restContentStyle;

        const content = (
            <StyledLightboxPanel
                ref={setRef}
                id={contentId}
                className={className}
                $cascade={cascade}
                {...getFloatingProps({
                    onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Escape" && onEscapeKeyDown) {
                            onEscapeKeyDown(e.nativeEvent);
                        }
                    },
                })}
                // transitionStyles after floatingStyles: `transform: false` is set
                // on the floating options, so floatingStyles positions via top/left
                // and leaves `transform` free for the enter/exit animation to own.
                style={{ ...floatingStyles, ...transitionStyles, ...(zIndex != null && { zIndex }), ...(overflow && { overflow }) }}
            >
                <StyledLightboxPanelContent $cascade={cascade} style={contentStyle}>
                    {title && <StyledLightboxTitle>{title}</StyledLightboxTitle>}
                    {children}
                </StyledLightboxPanelContent>
            </StyledLightboxPanel>
        );

        return (
            <FloatingPortal>
                {trapFocus ? (
                    <FloatingFocusManager
                        context={context}
                        modal={false}
                        initialFocus={initialFocus}
                        returnFocus
                    >
                        {content}
                    </FloatingFocusManager>
                ) : (
                    content
                )}
            </FloatingPortal>
        );
    }
);
